import React, { useState, useRef } from "react";
import {
  Button,
  Group,
  Transition,
  Text,
  Anchor,
  PinInput,
  Stack,
} from "@mantine/core";
import { TextInput, PasswordInput } from "react-hook-form-mantine";

import {
  useEmailSignInStart,
  useEmailSignInVerify,
  useLogin,
  useSendResetPasswordEmail,
} from "../../api/auth";
import { useForm } from "react-hook-form";
import { web_emailSignInSchema, web_regularLoginSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { applySignInResponse } from "../../services/authStore";
import { queryClient } from "../../services/react-query";

const CODE_LENGTH = 6;

function LoginForm() {
  const [mode, setMode] = useState("email"); // 'email', 'code', 'password', 'forgot'
  // The address the sign-in code went to, shown on the code step.
  const [sentTo, setSentTo] = useState(null);
  const [code, setCode] = useState("");
  const passwordInputRef = useRef(null);

  const handleEnterPassword = () => {
    setMode("password");
    // I have to fix the focus issue, because
    // react-hook-form-mantine component doesn't forward the refs
    setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 300);
  };

  const handleForgotPassword = () => {
    setMode("forgot");
  };

  const handleBackToLogin = () => {
    setMode("email");
  };

  const schema =
    mode === "password" ? web_regularLoginSchema : web_emailSignInSchema;

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { email: "", password: "" },
    resolver: resolver(schema),
    mode: "onBlur",
  });

  const { mutate, isPending, isSuccess, isError, error } = useLogin();
  const {
    mutate: sendSignInCode,
    isPending: sendingCode,
    isError: isSendCodeError,
    error: sendCodeError,
  } = useEmailSignInStart();
  const {
    mutate: verifySignInCode,
    isPending: verifyingCode,
    isError: isVerifyError,
    error: verifyError,
    reset: resetVerify,
  } = useEmailSignInVerify();

  const sendCodeTo = (email) =>
    sendSignInCode(
      { email },
      {
        onSuccess: () => {
          setCode("");
          resetVerify();
          setSentTo(email);
          setMode("code");
        },
      }
    );

  const submitCode = (value) => {
    if (value.length !== CODE_LENGTH || verifyingCode) return;
    verifySignInCode(
      { email: sentTo, code: value },
      {
        onSuccess: (data) => {
          applySignInResponse(data);
          queryClient.invalidateQueries();
        },
        onError: () => setCode(""),
      }
    );
  };
  const {
    mutate: sendResetPasswordEmail,
    isPending: sendingResetPasswordEmail,
    isSuccess: resetPasswordEmailSent,
    isError: isResetPasswordEmailError,
    error: resetPasswordEmailError,
  } = useSendResetPasswordEmail();

  const handleLogin = (data) => {
    if (mode === "email") {
      sendCodeTo(data.email);
      return;
    }

    if (mode === "code") {
      submitCode(code);
      return;
    }

    if (mode === "forgot") {
      sendResetPasswordEmail(data, {
        onSuccess: () => {
          reset({ email: "" });
        },
      });
      return;
    }

    mutate(
      {
        email: data.email,
        password: data.password,
      },
      {
        onSuccess: (data) => {
          applySignInResponse(data);
          queryClient.invalidateQueries();
        },
        onError: (error) => {
          console.error("Login failed:", error);
          reset({ email: data.email, password: "" });
          passwordInputRef.current?.focus();
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(handleLogin)}>
      {/* Email mode - initial state */}
      {mode === "email" && (
        <>
          <TextInput
            label="Email address"
            placeholder="hello@gmail.com"
            size="md"
            control={control}
            name="email"
            withAsterisk
          />
          <Group mt="md" grow>
            <Button
              size="sm"
              h="40px"
              type="submit"
              loading={sendingCode}
              color="#2b3846"
            >
              Email me a code
            </Button>
            <Button
              size="sm"
              onClick={handleEnterPassword}
              color="#2b3846"
              h="40px"
            >
              Enter Password
            </Button>
          </Group>
          {isSendCodeError && (
            <Text c="red" mt="md" fw="500">
              {sendCodeError?.message || "Could not send the code"}
            </Text>
          )}
        </>
      )}

      {mode === "code" && (
        <Stack gap="sm">
          <Text size="lg" fw="600">
            Check your email
          </Text>
          <Text size="sm" c="dimmed">
            We sent a 6-digit code to {sentTo}. It expires in 10 minutes.
          </Text>
          <Group justify="center" mt="xs">
            <PinInput
              // A sign-in code: never in session replays.
              className="ph-no-capture"
              length={CODE_LENGTH}
              type="number"
              oneTimeCode
              autoFocus
              size="md"
              value={code}
              onChange={setCode}
              onComplete={submitCode}
              disabled={verifyingCode}
              error={isVerifyError}
              aria-label="Sign-in code"
            />
          </Group>
          {isVerifyError && (
            <Text c="red" size="sm" ta="center">
              {verifyError?.message || "That code did not work"}
            </Text>
          )}
          <Button
            fullWidth
            size="sm"
            h="40px"
            type="submit"
            color="#2b3846"
            loading={verifyingCode}
            disabled={code.length !== CODE_LENGTH}
          >
            Sign in
          </Button>
          <Group justify="space-between">
            <Anchor
              href="#"
              onClick={(event) => {
                event.preventDefault();
                sendCodeTo(sentTo);
              }}
              c="#2b3846"
              size="sm"
            >
              {sendingCode ? "Sending..." : "Send a new code"}
            </Anchor>
            <Anchor
              href="#"
              onClick={(event) => {
                event.preventDefault();
                reset({ email: sentTo, password: "" });
                handleBackToLogin();
              }}
              c="#2b3846"
              size="sm"
            >
              Use another email
            </Anchor>
          </Group>
        </Stack>
      )}

      {/* Single transition for both password and forgot password modes */}
      <Transition
        mounted={mode === "password" || mode === "forgot"}
        transition="fade"
        duration={300}
        timingFunction="ease"
      >
        {(styles) => (
          <div style={styles}>
            {mode === "password" && (
              <>
                <TextInput
                  label="Email address"
                  placeholder="hello@gmail.com"
                  size="md"
                  control={control}
                  name="email"
                  withAsterisk
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  mt="md"
                  size="md"
                  ref={passwordInputRef}
                  withAsterisk
                  control={control}
                  name="password"
                />

                <Anchor
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    handleForgotPassword();
                  }}
                  c="#2b3846"
                  size="sm"
                  mt="xs"
                  py="xs"
                  style={{ display: "block", textAlign: "center" }}
                >
                  Forgot password?
                </Anchor>

                <Button
                  fullWidth
                  mt="sm"
                  size="sm"
                  type="submit"
                  loading={isPending}
                  color="#2b3846"
                  h="40px"
                  disabled={isSuccess}
                >
                  Login
                </Button>

                {isSuccess && (
                  <Text c="green" mt="md">
                    Login successful!
                  </Text>
                )}

                {isError && (
                  <Text c="red" mt="md">
                    {error?.message || "Login failed"}
                  </Text>
                )}
              </>
            )}

            {mode === "forgot" && (
              <>
                <Text size="lg" fw="600">
                  Reset Password
                </Text>
                <Text size="sm" c="dimmed" mb="md">
                  Enter your email address and we'll send you a link to reset
                  your password.
                </Text>

                <TextInput
                  label="Email address"
                  placeholder="hello@gmail.com"
                  size="md"
                  control={control}
                  name="email"
                  withAsterisk
                />

                {resetPasswordEmailSent && (
                  <Text c="green" mt="md" fw="500">
                    Reset password email sent! Please check your inbox.
                  </Text>
                )}

                {isResetPasswordEmailError && (
                  <Text c="red" mt="md" fw="500">
                    {resetPasswordEmailError?.message ||
                      "Failed to send reset password email"}
                  </Text>
                )}

                <Button
                  fullWidth
                  mt="md"
                  size="sm"
                  type="submit"
                  color="#2b3846"
                  h="40px"
                  loading={sendingResetPasswordEmail}
                >
                  Send Reset Link
                </Button>

                <Anchor
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    handleBackToLogin();
                  }}
                  c="#2b3846"
                  size="sm"
                  mt="md"
                  style={{ display: "block", textAlign: "center" }}
                >
                  Back to Login
                </Anchor>
              </>
            )}
          </div>
        )}
      </Transition>
    </form>
  );
}

export default LoginForm;
